import {
  assertNever,
  toNumber,
  WebsocketProjectData,
} from '@freelancer/datastore/core';
import { NewsfeedApiResponseEntry } from './notifications.backend-model';
import {
  isNotificationEntry,
  NotificationEntry,
  NotificationEntrySkipped,
  NotificationProjectData,
} from './notifications.model';

export function isTest(entry: NewsfeedApiResponseEntry) {
  switch (entry.type) {
    case 'contest':
    case 'failingProject':
    case 'inviteUserBid':
    case 'localJobPosted':
    case 'notifyfollower':
    case 'project':
    case 'quickHireProject':
    case 'recruiterProject':
    case 'xpContest':
      // case 'localJobsMobile':
      return entry.data.isTest;
    default:
      return false;
  }
}

function notificationEntrySkipped(id: string): NotificationEntrySkipped {
  return {
    type: undefined,
    parent_type: 'notifications',
    id,
  };
}

export function transformNotification(
  entry: NewsfeedApiResponseEntry,
  userId: string,
): NotificationEntry | NotificationEntrySkipped {
  if (isTest(entry)) {
    return notificationEntrySkipped(entry.id);
  }

  switch (entry.type) {
    // There are:
    // - Types that shouldn't be in notification feed
    case 'contest':
    case 'failingProject':
    case 'localJobPosted':
    case 'project':
    case 'recruiterProject':
    case 'xpContest':
      return notificationEntrySkipped(entry.id);
    // Types that are not implemented
    case 'contestExpired':
    case 'serviceStatusChanged':
      return notificationEntrySkipped(entry.id);
    // These types are old and ignored.
    // TODO: bidHidden is not implemented yet because it's an AB test. See T38861.
    case 'contactRequestSent':
    case 'contactRequestReceived':
    case 'bidHidden':
    case 'sitesBinCreated':
    case 'sitesHandOverMessageSent':
    case 'sitesListingStatusChanged':
    case 'sitesMessageSent':
    case 'sitesOfferCreated':
      return notificationEntrySkipped(entry.id);

    case 'articleCommentReceived':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          articleAuthor: toNumber(entry.data.article_author), // The ID of author
          articleName: entry.data.article_name,
          articleType: entry.data.article_type, // Some enum?
          commentBy: toNumber(entry.data.comment_by),
          commentId: toNumber(entry.data.comment_id),
          comment: entry.data.comment,
          imgUrl: entry.data.imgUrl,
          linkUrl: entry.data.linkUrl,
          publicName: entry.data.publicName,
          time: toNumber(entry.data.time) * 1000,
          user: entry.data.user,
        },
      };
    case 'award':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          imgUrl: entry.data.imgUrl,
          linkUrl: entry.data.linkUrl,
          time: entry.data.time * 1000,
          title: entry.data.title,
          username: entry.data.userName,
        },
      };
    case 'awardBadge':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          descr: entry.data.descr,
          imgUrl: undefined,
          linkUrl: entry.data.linkUrl,
          name: entry.data.name,
          time: entry.data.time * 1000,
        },
      };
    case 'awardCredit':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          amount: toNumber(entry.data.amount),
          imgUrl: undefined,
          linkUrl: entry.data.linkUrl,
          time: entry.data.time * 1000,
        },
      };
    case 'awardReminder':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          imgUrl: entry.data.imgUrl,
          linkUrl: `${entry.data.linkUrl}?gotoBid=${entry.data.bid.id}`,
          name: entry.data.name,
          time: entry.data.time * 1000,
          username: entry.data.userName,
        },
      };
    case 'awardMilestoneReminder':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          appendedDescr: entry.data.appended_descr,
          currencyCode: entry.data.currencycode,
          currencyId: entry.data.currencyid,
          currencySign: entry.data.currencysign,
          imgUrl: entry.data.imgUrl,
          linkUrl: entry.data.linkUrl,
          time: entry.data.time * 1000,
          title: entry.data.title,
          userId: toNumber(entry.data.userId),
          username: entry.data.userName,
        },
      };
    case 'awardXp':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          amount: toNumber(entry.data.amount),
          descr: entry.data.descr,
          imgUrl: undefined,
          linkUrl: entry.data.linkUrl,
          time: entry.data.time * 1000,
        },
      };
    case 'bid':
    case 'bid_self':
    case 'bid_updated':
      return {
        type: 'bid',
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          amount: entry.data.amount,
          bidAvg: toNumber(entry.data.bidAvg),
          bidCount: entry.data.bidCount,
          currency: entry.data.currency,
          imgUrl: entry.data.imgUrl,
          linkUrl: `${entry.data.linkUrl}?gotoBid=${entry.data.bid.id}`,
          projName: entry.data.projName,
          publicName: entry.data.publicName,
          time: entry.data.time * 1000,
          title: entry.data.title,
          userId: toNumber(entry.data.userId),
          username: entry.data.userName,
        },
      };
    case 'completed': {
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          amount: toNumber(entry.data.amount),
          categoryName: entry.data.categoryName,
          completeStatus: entry.data.completeStatus,
          currencyCode: entry.data.currencycode,
          currencyId: toNumber(entry.data.currencyid),
          currencySign: entry.data.currencysign,
          employerId: toNumber(entry.data.employerId),
          employerPublicName: entry.data.employerPublicName,
          employerUsername: entry.data.employerUsername,
          expiredTimestamp: entry.data.expiredTimestamp,
          freelancerId: toNumber(entry.data.freelancerId),
          freelancerPublicName: entry.data.freelancerPublicName,
          freelancerUsername: entry.data.freelancerUsername,
          imgUrl:
            entry.data.employerId === userId
              ? entry.data.img_f.profile_logo_url
              : entry.data.img_e.profile_logo_url,
          name: entry.data.name,
          // projIsHourly: entry.data.projIsHourly,
          selectionId: toNumber(entry.data.selectionId),
          seoUrl: entry.data.seoUrl,
          state: entry.data.state,
          submitDate: entry.data.submitDate,
          time: entry.data.time * 1000,
          linkUrl: `/users/review/rate.php?id=${entry.data.id}&to_user=${
            entry.data.employerId === userId
              ? entry.data.freelancerId
              : entry.data.employerId
          }`,
        },
      };
    }
    case 'contestAwardedToEmployer':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          contestName: entry.data.contestName,
          contestSeoUrl: entry.data.contestSEOUrl,
          currencyCode: entry.data.currencyCode,
          currencySign: entry.data.currencySign,
          entryCount: entry.data.entryCount,
          entryId: entry.data.entry_id,
          entryNumber: entry.data.entry_number,
          freelancerCount: entry.data.freelancerCount,
          imgUrl: entry.data.imgUrl,
          isBought: entry.data.isBought,
          linkUrl: entry.data.linkUrl,
          prize: entry.data.prize,
          publicName: entry.data.publicName,
          thumb: entry.data.thumb,
          time: entry.data.time * 1000,
          userId: entry.data.userId,
          username: entry.data.userName,
        },
      };
    case 'contestAwardedToFreelancer':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          contestName: entry.data.contestName,
          contestSeoUrl: entry.data.contestSEOURL,
          currencyCode: entry.data.currencyCode,
          currencySign: entry.data.currencySign,
          entryCount: entry.data.entryCount,
          entryId: entry.data.entry_id,
          entryNumber: entry.data.entry_number,
          freelancerCount: entry.data.freelancerCount,
          imgUrl: entry.data.imgUrl,
          linkUrl: entry.data.linkUrl,
          prize: entry.data.prize,
          publicName: entry.data.publicName,
          time: entry.data.time * 1000,
          userId: entry.data.userId,
          username: entry.data.userName,
        },
      };
    case 'contestEntry':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          contestHolder: entry.data.contestHolder,
          contestId: entry.data.contestId,
          contestName: entry.data.contestName,
          firstUser: entry.data.firstUser,
          firstUserPublicName: entry.data.firstUserPublicName,
          imgUrl: entry.data.imgUrl,
          linkUrl: entry.data.linkUrl,
          secondUser: entry.data.secondUser || undefined,
          secondUserPublicName: entry.data.secondUserPublicName || undefined,
          time: entry.data.time * 1000,
          userId: toNumber(entry.data.userId),
        },
      };
    case 'contestEntryBoughtToEmployer':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          contestName: entry.data.contestName,
          entryId: entry.data.entry_id,
          entryNumber: entry.data.entry_number,
          imgUrl: entry.data.imgUrl,
          linkUrl: entry.data.linkUrl,
          publicName: entry.data.publicName,
          time: entry.data.time * 1000,
          username: entry.data.userName,
        },
      };
    case 'contestEntryBoughtToFreelancer':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          contestName: entry.data.contestName,
          entryId: entry.data.entry_id,
          entryNumber: entry.data.entry_number,
          imgUrl: entry.data.imgUrl,
          linkUrl: entry.data.linkUrl,
          publicName: entry.data.publicName,
          time: entry.data.time * 1000,
          username: entry.data.userName,
        },
      };
    case 'contestEntryCommentedToFreelancer': {
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          contestId: toNumber(entry.data.contestId),
          contestName: entry.data.contestName,
          entryNumber: toNumber(entry.data.entryNumber),
          imgUrl: entry.data.imgUrl,
          linkUrl: entry.data.linkUrl,
          time: toNumber(entry.data.time) * 1000,
        },
      };
    }
    case 'contestPCBNotification': {
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          contestHolder: entry.data.contestHolder,
          contestId: entry.data.contestId,
          contestName: entry.data.contestName,
          firstUser: entry.data.firstUser,
          firstUserPublicName: entry.data.firstUserPublicName,
          imgUrl: entry.data.imgUrl,
          linkUrl: entry.data.linkUrl,
          secondUser: entry.data.secondUser || undefined,
          secondUserPublicName: entry.data.secondUserPublicName || undefined,
          time: entry.data.time * 1000,
          userId: entry.data.userId,
        },
      };
    }
    case 'contestPCBNotificationFullView': {
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          contestHolder: entry.data.contestHolder,
          contestName: entry.data.contestName,
          entryNumber: toNumber(entry.data.entryNumber),
          firstUser: entry.data.firstUser,
          firstUserPublicName: entry.data.firstUserPublicName,
          imgUrl: entry.data.imgUrl,
          linkUrl: entry.data.linkUrl,
          secondUser: entry.data.secondUser || undefined,
          secondUserPublicName: entry.data.secondUserPublicName || undefined,
          time: entry.data.time * 1000,
          userId: entry.data.userId,
        },
      };
    }
    case 'contestEntryRatedToFreelancer': {
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          action: entry.data.action,
          contestId: toNumber(entry.data.contestId),
          contestName: entry.data.contestName,
          employerId: toNumber(entry.data.employerId),
          entriesUrl: entry.data.entriesUrl,
          entryId: toNumber(entry.data.entryId),
          entryNumber: toNumber(entry.data.entryNumber),
          entryS3path: entry.data.entryS3path,
          entryThumb: entry.data.entryThumb,
          entryUrl: entry.data.entryUrl,
          freelancerId: toNumber(entry.data.freelancerId),
          hasOtherEntriesRatedOrRejected:
            entry.data.hasOtherEntriesRatedOrRejected,
          imgUrl: entry.data.imgUrl,
          linkUrl: entry.data.linkUrl,
          ratedOrRejectedEntriesCount: toNumber(
            entry.data.ratedOrRejectedEntriesCount,
          ),
          rating: entry.data.rating,
          time: toNumber(entry.data.time) * 1000,
        },
      };
    }
    case 'contestEntryReconsideredToFreelancer': {
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          action: entry.data.action,
          contestId: toNumber(entry.data.contestId),
          contestName: entry.data.contestName,
          employerId: toNumber(entry.data.employerId),
          entriesUrl: entry.data.entriesUrl,
          entryId: toNumber(entry.data.entryId),
          entryNumber: toNumber(entry.data.entryNumber),
          entryS3path: entry.data.entryS3path,
          entryThumb: entry.data.entryThumb,
          entryUrl: entry.data.entryUrl,
          freelancerId: toNumber(entry.data.freelancerId),
          hasOtherEntriesRatedOrRejected:
            entry.data.hasOtherEntriesRatedOrRejected,
          imgUrl: entry.data.imgUrl,
          linkUrl: entry.data.linkUrl,
          ratedOrRejectedEntriesCount: toNumber(
            entry.data.ratedOrRejectedEntriesCount,
          ),
          rating: entry.data.rating,
          time: toNumber(entry.data.time) * 1000,
        },
      };
    }
    case 'contestEntryRejectedToFreelancer': {
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          action: entry.data.action,
          contestId: toNumber(entry.data.contestId),
          contestName: entry.data.contestName,
          employerId: toNumber(entry.data.employerId),
          entriesUrl: entry.data.entriesUrl,
          entryId: toNumber(entry.data.entryId),
          entryNumber: toNumber(entry.data.entryNumber),
          entryS3path: entry.data.entryS3path,
          entryThumb: entry.data.entryThumb,
          entryUrl: entry.data.entryUrl,
          freelancerId: toNumber(entry.data.freelancerId),
          hasOtherEntriesRatedOrRejected:
            entry.data.hasOtherEntriesRatedOrRejected,
          imgUrl: entry.data.imgUrl,
          linkUrl: entry.data.linkUrl,
          ratedOrRejectedEntriesCount: toNumber(
            entry.data.ratedOrRejectedEntriesCount,
          ),
          rating: entry.data.rating,
          time: toNumber(entry.data.time) * 1000,
        },
      };
    }
    case 'createMilestone':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          ...transformProjectData(entry.data),
          accountId: toNumber(entry.data.account_id),
          amount: entry.data.amount,
          appendedDescr: entry.data.appended_descr,
          bidId: entry.data.bidId,
          currencyCode: entry.data.currencycode,
          currencySign: entry.data.currencysign,
          projectId: entry.data.project_id,
          shortDescr: entry.data.shortDescr,
          transId: entry.data.transId,
          time: entry.data.time * 1000,
          username: entry.data.userName,
        },
      };
    case 'denyed':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          imgUrl: entry.data.imgUrl,
          jobsURL: entry.data.jobsURL,
          linkUrl: entry.data.linkUrl,
          name: entry.data.name,
          publicName: entry.data.publicName,
          seoUrl: entry.data.seoUrl,
          time: entry.data.time * 1000,
          userId: entry.data.userId,
          username: entry.data.userName,
        },
      };
    case 'draftContest':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          contestEditUrl: entry.data.contestEditUrl,
          contestId: entry.data.contestId,
          contestName: entry.data.contestName,
          imgUrl: entry.data.imgUrl,
          isPublished: entry.data.isPublished,
          linkUrl: entry.data.contestUrl,
          time: entry.data.time * 1000,
        },
      };
    case 'highLtvAnnualDiscount':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          imgUrl: undefined,
          linkUrl: entry.data.linkUrl,
          time: entry.data.time * 1000,
          userDisplayName: entry.data.userDisplayName,
        },
      };
    case 'inviteUserBid':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          ...transformProjectData(entry.data),
          currencyCode: entry.data.currencycode,
          currencySign: entry.data.currencysign,
          inviteeId: toNumber(entry.data.inviteeId),
          inviteeName: entry.data.inviteeName,
          maxBudget: toNumber(entry.data.maxBudget),
          minBudget: toNumber(entry.data.minBudget),
          publicName: entry.data.publicName,
          time: entry.data.time * 1000,
        },
      };
    case 'inviteToContest':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          buyer: entry.data.buyer,
          buyerName: entry.data.buyerName,
          buyerPublicName: entry.data.buyerPublicName,
          buyerUrl: entry.data.buyerUrl,
          contestRecommendedSkills: entry.data.contest_recommended_skills,
          currencyCode: entry.data.currencycode,
          currencySign: entry.data.currencysign,
          description: entry.data.description,
          imgUrl: entry.data.imgUrl,
          linkUrl: entry.data.linkUrl,
          name: entry.data.name,
          prize: entry.data.prize,
          sellerId: entry.data.sellerId,
          sellerName: entry.data.sellerName,
          sellerPublicName: entry.data.sellerPublicName,
          time: entry.data.time * 1000,
          title: entry.data.title,
          type: entry.data.type || undefined,
        },
      };
    case 'invoiceRequested':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          accountId: toNumber(entry.data.accountId),
          amount: toNumber(entry.data.amount),
          bidId: toNumber(entry.data.bidId),
          currencyCode: entry.data.currencyCode,
          currencySign: entry.data.currencySign,
          imgUrl: entry.data.imgUrl,
          invoiceId: toNumber(entry.data.invoiceId),
          linkUrl: `${entry.data.linkUrl}#invoices_${entry.data.bidId}`,
          name: entry.data.name,
          publicName: entry.data.publicName,
          sellerId: toNumber(entry.data.sellerId),
          seoUrl: entry.data.seoUrl,
          time: toNumber(entry.data.time) * 1000,
          userId: toNumber(entry.data.userId),
          username: entry.data.userName,
        },
      };
    case 'levelUp':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          imgUrl: undefined,
          level: entry.data.level,
          linkUrl: entry.data.linkUrl,
          membership: entry.data.membership,
          perks: entry.data.perks,
          time: entry.data.time * 1000,
          username: entry.data.username,
        },
      };
    case 'notifyfollower':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: transformProjectData(entry.data),
      };
    case 'hireMe':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          currency: entry.data.currency,
          imgUrl: entry.data.imgUrl,
          linkUrl: entry.data.linkUrl,
          period: toNumber(entry.data.period),
          projIsHourly: entry.data.projIsHourly,
          publicName: entry.data.publicName,
          sum: toNumber(entry.data.sum),
          title: entry.data.title,
          time: entry.data.time * 1000,
          username: entry.data.userName,
        },
      };
    case 'quickHireProject':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          ...transformProjectData(entry.data),
          currency: entry.data.currency,
          period: toNumber(entry.data.period),
          projIsHourly: entry.data.projIsHourly,
          publicName: entry.data.publicName,
          sum: toNumber(entry.data.sum),
        },
      };
    case 'releaseMilestone':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          accountId: entry.data.accountId,
          amount: entry.data.amount,
          currencyCode: entry.data.currencycode,
          currencySign: entry.data.currencysign,
          imgUrl: entry.data.imgUrl,
          linkUrl: entry.data.linkUrl,
          name: entry.data.name,
          otherReason: entry.data.otherReason,
          publicName: entry.data.publicName,
          seoUrl: entry.data.seoUrl,
          submitDate: entry.data.submitDate,
          time: entry.data.time * 1000,
          title: entry.data.title,
          userId: toNumber(entry.data.userId),
          username: entry.data.userName,
        },
      };
    case 'requestEndProject':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          accountId: entry.data.accountId,
          bidId: entry.data.bidId,
          categoryName: entry.data.categoryName,
          dayCount: entry.data.dayCount,
          imgUrl: entry.data.imgUrl,
          linkUrl: entry.data.linkUrl,
          name: entry.data.name,
          publicName: entry.data.publicName,
          seoUrl: entry.data.seoUrl,
          submitDate: entry.data.submitDate,
          time: entry.data.time * 1000,
          userId: entry.data.userId,
          username: entry.data.userName,
        },
      };
    case 'requestMilestone': {
      const linkUrl = !entry.data.linkUrl
        ? `/projects/${entry.data.seoUrl}.html`
        : `${entry.data.linkUrl}#invoices_${entry.data.bidId}`;

      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          accountId: toNumber(entry.data.accountId),
          amount: toNumber(entry.data.amount),
          bidId: toNumber(entry.data.bidId),
          currencyCode: entry.data.currencyCode,
          currencyId: toNumber(entry.data.currencyId),
          currencySign: entry.data.currencySign,
          description: entry.data.description,
          imgUrl: entry.data.imgUrl,
          linkUrl,
          name: entry.data.name,
          requestId: toNumber(entry.data.requestId),
          sellerId: toNumber(entry.data.sellerId),
          seoUrl: entry.data.seoUrl,
          submitDate: entry.data.submitDate,
          time: entry.data.time * 1000,
          userId: toNumber(entry.data.userId),
          username: entry.data.userName,
        },
      };
    }
    case 'requestToRelease': {
      const linkUrl = !entry.data.linkUrl
        ? `/projects/${entry.data.seoUrl}.html`
        : `${entry.data.linkUrl}#invoices_${entry.data.bidId}`;

      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          accountId: toNumber(entry.data.accountId),
          amount: toNumber(entry.data.amount),
          bidId: toNumber(entry.data.bidId),
          currencyCode: entry.data.currencycode,
          currencySign: entry.data.currencysign,
          descr: entry.data.descr,
          // img: ???,
          imgUrl: entry.data.imgUrl,
          linkUrl,
          name: entry.data.name,
          publicName: entry.data.publicName,
          seoUrl: entry.data.seoUrl,
          state: entry.data.state,
          time: toNumber(entry.data.time) * 1000,
          tranId: toNumber(entry.data.tranId),
          userId: toNumber(entry.data.userId),
          username: entry.data.userName,
        },
      };
    }
    case 'serviceProjectEndingSeller':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          bidId: toNumber(entry.data.bid_id),
          buyerId: toNumber(entry.data.buyer_id),
          buyerName: entry.data.buyer_name,
          hoursRemaining: toNumber(entry.data.hours_remaining),
          imgUrl: undefined,
          linkUrl: entry.data.project_url,
          orderStartDate: entry.data.order_start_date,
          projectId: toNumber(entry.data.project_id),
          projectName: entry.data.project_name,
          projectUrl: entry.data.project_url,
          sellerId: toNumber(entry.data.seller_id),
          sellerName: entry.data.seller_name,
          serviceId: toNumber(entry.data.service_id),
          serviceName: entry.data.service_name,
          time: entry.data.time * 1000,
        },
      };
    case 'serviceProjectPostedSeller':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          bidId: toNumber(entry.data.bid_id),
          buyerId: toNumber(entry.data.buyer_id),
          buyerImgUrl: entry.data.buyer_img_url,
          buyerName: entry.data.buyer_name,
          buyerPublicName: entry.data.buyer_public_name,
          currencyCode: entry.data.currency_code,
          currencySign: entry.data.currency_sign,
          imgUrl: entry.data.buyer_img_url,
          linkUrl: entry.data.project_url,
          orderEndDate: entry.data.order_end_date,
          orderStartDate: entry.data.order_start_date,
          projectCost: toNumber(entry.data.project_cost),
          // projectDuration: entry.data.project_duration,
          projectId: toNumber(entry.data.project_id),
          // projectMilestones: entry.data.project_milestones,
          projectName: entry.data.project_name,
          projectUrl: entry.data.project_url,
          sellerId: toNumber(entry.data.seller_id),
          sellerImgUrl: entry.data.seller_img_url,
          sellerName: entry.data.seller_name,
          sellerPublicName: entry.data.seller_public_name,
          serviceId: toNumber(entry.data.service_id),
          serviceName: entry.data.service_name,
          serviceUrl: entry.data.service_url,
          time: toNumber(entry.data.time) * 1000,
        },
      };
    case 'serviceApproved':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          imgUrl: undefined,
          linkUrl: entry.data.service_url,
          serviceId: toNumber(entry.data.service_id),
          serviceName: entry.data.service_name,
          serviceUrl: entry.data.service_url,
          serviceDuration: entry.data.service_duration,
          serviceCost: toNumber(entry.data.service_cost),
          currencySign: entry.data.currency_sign,
          currencyCode: entry.data.currency_code,
          serviceImg: entry.data.service_img, // A URL
          time: entry.data.time * 1000,
        },
      };
    case 'serviceRejected':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          imgUrl: undefined,
          linkUrl: entry.data.service_url,
          serviceId: toNumber(entry.data.service_id),
          serviceName: entry.data.service_name,
          serviceUrl: entry.data.service_url,
          serviceDuration: entry.data.service_duration,
          serviceCost: toNumber(entry.data.service_cost),
          currencySign: entry.data.currency_sign,
          currencyCode: entry.data.currency_code,
          serviceImg: entry.data.service_img, // A URL
          time: entry.data.time * 1000,
        },
      };
    case 'signUpFreeTrialUpsell':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          imgUrl: undefined,
          linkUrl: entry.data.linkUrl,
          time: entry.data.time * 1000,
          trialPackageName: entry.data.trialPackageName,
        },
      };
    case 'upsellCrowdsourceFinding':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.id,
        data: {
          imgUrl: undefined,
          linkUrl: undefined,
          projectName: entry.data.project_name,
          time: entry.data.time * 1000,
        },
      };
    default:
      return assertNever(entry);
  }
}

export function transformWebsocketNotification(
  entry: NewsfeedApiResponseEntry,
  userId: string,
): NotificationEntry | undefined {
  const transformed = transformNotification(entry, userId);
  return isNotificationEntry(transformed) ? transformed : undefined;
}

export function transformProjectData(
  data: WebsocketProjectData,
): NotificationProjectData {
  return {
    appendedDescr: data.appended_descr,
    imgUrl: data.imgUrl,
    isTest: data.isTest,
    jobs: data.jobs ? data.jobs.map(Number) : [],
    jobString: data.jobString,
    linkUrl: data.linkUrl,
    showBudgetMinimallyPostedProject: data.showBudgetMinimallyPostedProject,
    text: data.text,
    time: data.time * 1000,
    title: data.title,
    userId: toNumber(data.userId),
    username: data.userName,
  };
}



// WEBPACK FOOTER //
// ./src/@freelancer/datastore/notifications/notifications.transformers.ts